'use client';

import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import { Check } from 'lucide-react';
import { cn } from '../lib/utils';

export interface DropdownMenuItem {
  label: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  disabled?: boolean;
  destructive?: boolean;
  separator?: boolean;
  checked?: boolean;
  radioGroup?: string;
  href?: string;
}

export interface DropdownMenuProps {
  trigger: React.ReactNode;
  items: DropdownMenuItem[];
  align?: 'left' | 'right';
  side?: 'top' | 'bottom';
  className?: string;
  navigate?: (href: string) => void;
}

export function DropdownMenu({
  trigger,
  items,
  align = 'right',
  side = 'bottom',
  className,
  navigate,
}: DropdownMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const close = useCallback(() => {
    setIsOpen(false);
    setHighlightedIndex(-1);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((open) => !open);
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close();
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, close]);

  const actionableItems = items.filter((item) => !item.separator);

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else if (highlightedIndex >= 0) {
          const item = actionableItems[highlightedIndex];
          if (item && !item.disabled) {
            if (item.href) {
              if (navigate) {
                navigate(item.href);
              } else {
                window.location.assign(item.href);
              }
            } else {
              item.onClick?.();
            }
            close();
          }
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex((prev) => {
            let next = prev + 1;
            while (next < actionableItems.length && actionableItems[next]?.disabled) next++;
            return next < actionableItems.length ? next : prev;
          });
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex((prev) => {
            let next = prev - 1;
            while (next >= 0 && actionableItems[next]?.disabled) next--;
            return next >= 0 ? next : prev;
          });
        }
        break;
      case 'Escape':
        close();
        break;
      default:
        break;
    }
  };

  const triggerAriaProps = {
    'aria-haspopup': 'menu' as const,
    'aria-expanded': isOpen,
    'aria-controls': isOpen ? menuId : undefined,
  };

  const renderedTrigger = React.isValidElement(trigger) ? (
    React.cloneElement(trigger, {
      ...triggerAriaProps,
      onClick: (e: React.MouseEvent) => {
        toggle();
        (trigger.props as { onClick?: (e: React.MouseEvent) => void }).onClick?.(e);
      },
      onKeyDown: (e: React.KeyboardEvent) => {
        handleTriggerKeyDown(e);
        (trigger.props as { onKeyDown?: (e: React.KeyboardEvent) => void }).onKeyDown?.(e);
      },
    } as Record<string, unknown>)
  ) : (
    <button
      type="button"
      {...triggerAriaProps}
      onClick={toggle}
      onKeyDown={handleTriggerKeyDown}
      className="inline-flex min-w-0 items-center"
    >
      {trigger}
    </button>
  );

  let actionableIndex = -1;

  const renderMenuItem = (item: DropdownMenuItem, idx: number) => {
    if (item.separator) {
      return <div key={`sep-${idx}`} className="my-1 h-px bg-slate-200" role="separator" />;
    }

    actionableIndex++;
    const currentActionIndex = actionableIndex;
    const role = typeof item.checked === 'boolean' ? 'menuitemradio' : 'menuitem';
    const itemClassName = cn(
      'flex w-full min-w-0 items-center gap-2 px-3 py-2 text-left font-body text-body-sm transition-interactive duration-200',
      currentActionIndex === highlightedIndex && 'bg-surface-soft',
      item.destructive ? 'text-red-600 hover:bg-red-50' : 'text-text-heading',
      item.disabled && 'cursor-not-allowed opacity-50',
    );
    const itemContent = (
      <>
        {item.icon && <span className="shrink-0 [&_svg]:h-4 [&_svg]:w-4">{item.icon}</span>}
        <span className="min-w-0 break-words">{item.label}</span>
        {typeof item.checked === 'boolean' && (
          <Check
            aria-hidden="true"
            className={cn('ml-auto h-4 w-4 shrink-0', !item.checked && 'opacity-0')}
          />
        )}
      </>
    );

    if (item.href && !item.disabled) {
      return (
        <a
          key={`item-${idx}`}
          href={item.href}
          role={role}
          aria-checked={typeof item.checked === 'boolean' ? item.checked : undefined}
          onClick={(event) => {
            if (
              navigate &&
              event.button === 0 &&
              !event.metaKey &&
              !event.ctrlKey &&
              !event.shiftKey &&
              !event.altKey
            ) {
              event.preventDefault();
              navigate(item.href as string);
            }
            close();
          }}
          onMouseEnter={() => setHighlightedIndex(currentActionIndex)}
          className={itemClassName}
        >
          {itemContent}
        </a>
      );
    }

    return (
      <button
        key={`item-${idx}`}
        type="button"
        role={role}
        aria-checked={typeof item.checked === 'boolean' ? item.checked : undefined}
        disabled={item.disabled}
        onClick={() => {
          if (!item.disabled) {
            item.onClick?.();
            close();
          }
        }}
        onMouseEnter={() => setHighlightedIndex(currentActionIndex)}
        className={itemClassName}
      >
        {itemContent}
      </button>
    );
  };

  return (
    <div ref={containerRef} className={cn('relative inline-block min-w-0', className)}>
      {renderedTrigger}

      {isOpen && (
        <div
          id={menuId}
          ref={menuRef}
          role="menu"
          className={cn(
            'absolute z-50 min-w-[180px] overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg',
            'max-w-[calc(100vw-1rem)]',
            align === 'right' ? 'right-0' : 'left-0',
            side === 'top' ? 'bottom-full mb-1' : 'mt-1',
          )}
        >
          {items.map((item, idx) => {
            if (!item.radioGroup) return renderMenuItem(item, idx);
            if (idx > 0 && items[idx - 1]?.radioGroup === item.radioGroup) return null;

            const groupedItems: Array<{ item: DropdownMenuItem; idx: number }> = [];
            for (let groupIndex = idx; groupIndex < items.length; groupIndex++) {
              const groupedItem = items[groupIndex];
              if (!groupedItem || groupedItem.radioGroup !== item.radioGroup) break;
              groupedItems.push({ item: groupedItem, idx: groupIndex });
            }

            return (
              <div
                key={`group-${item.radioGroup}-${idx}`}
                role="group"
                aria-label={item.radioGroup}
              >
                {groupedItems.map(({ item: groupedItem, idx: groupedIndex }) =>
                  renderMenuItem(groupedItem, groupedIndex),
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
